/**
 * Visual Blocks Language
 *
 * Copyright 2012 Google Inc.
 * http://blockly.googlecode.com/
 * and 2014 Massachusetts Institute of Technology
 * http://zerorobotics.org/
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Generating C++ for text blocks.
 * Modified from the standard Blockly JavaScript generator.
 *
 * Converted from the legacy Zero Robotics Blockly generator
 * to the modern STEMX365 zrCppGenerator.
 *
 * @author q.neutron@gmail.com (Quynh Neutron)
 * @author dininno@mit.edu (Ethan DiNinno)
 */

import { zrCppGenerator } from "./zrCppGenerator";


// ============================================================
// controls_if
// ============================================================

zrCppGenerator.forBlock["controls_if"] = function(block) {

    // If/elseif/else condition.
    let n = 0;

    let argument =
        this.valueToCode(
            block,
            "IF" + n,
            this.ORDER_NONE
        ) || "false";

    let branch =
        this.statementToCode(
            block,
            "DO" + n
        );

    let code =
        "if (" +
        argument +
        ") {\n" +
        branch +
        "}";

    for (n = 1; n <= block.elseifCount_; n++) {

        argument =
            this.valueToCode(
                block,
                "IF" + n,
                this.ORDER_NONE
            ) || "false";

        branch =
            this.statementToCode(
                block,
                "DO" + n
            );

        code +=
            " else if (" +
            argument +
            ") {\n" +
            branch +
            "}";
    }

    if (block.elseCount_) {

        branch =
            this.statementToCode(
                block,
                "ELSE"
            );

        code +=
            " else {\n" +
            branch +
            "}";
    }

    return code + "\n";
};


// ============================================================
// logic_compare
// ============================================================

zrCppGenerator.forBlock["logic_compare"] = function(block) {

    // Comparison operator.

    const OPERATORS = {
        EQ: "==",
        NEQ: "!=",
        LT: "<",
        LTE: "<=",
        GT: ">",
        GTE: ">="
    };

    const operator =
        OPERATORS[
            block.getFieldValue("OP")
        ];

    const order =
        (operator === "==" || operator === "!=")
            ? this.ORDER_EQUALITY
            : this.ORDER_RELATIONAL;

    const argument0 =
        this.valueToCode(
            block,
            "A",
            order
        ) || "0";

    const argument1 =
        this.valueToCode(
            block,
            "B",
            order
        ) || "0";

    const code =
        argument0 +
        " " +
        operator +
        " " +
        argument1;

    return [
        code,
        order
    ];
};


// ============================================================
// logic_operation
// ============================================================

zrCppGenerator.forBlock["logic_operation"] = function(block) {

    // Operations "and", "or".

    const operator =
        block.getFieldValue("OP") === "AND"
            ? "&&"
            : "||";

    const order =
        operator === "&&"
            ? this.ORDER_LOGICAL_AND
            : this.ORDER_LOGICAL_OR;

    let argument0 =
        this.valueToCode(
            block,
            "A",
            order
        );

    let argument1 =
        this.valueToCode(
            block,
            "B",
            order
        );

    if (!argument0 && !argument1) {

        // If there are no arguments,
        // then the return value is false.

        argument0 = "false";
        argument1 = "false";

    } else {

        // Single missing arguments have
        // no effect on the return value.

        const defaultArgument =
            operator === "&&"
                ? "true"
                : "false";

        if (!argument0) {
            argument0 = defaultArgument;
        }

        if (!argument1) {
            argument1 = defaultArgument;
        }
    }

    const code =
        argument0 +
        " " +
        operator +
        " " +
        argument1;

    return [
        code,
        order
    ];
};


// ============================================================
// logic_negate
// ============================================================

zrCppGenerator.forBlock["logic_negate"] = function(block) {

    // Negation.

    const order =
        this.ORDER_LOGICAL_NOT;

    const argument0 =
        this.valueToCode(
            block,
            "BOOL",
            order
        ) || "true";

    const code =
        "!" +
        argument0;

    return [
        code,
        order
    ];
};


// ============================================================
// logic_boolean
// ============================================================

zrCppGenerator.forBlock["logic_boolean"] = function(block) {

    // Boolean values true and false.

    const code =
        block.getFieldValue("BOOL") === "TRUE"
            ? "true"
            : "false";

    return [
        code,
        this.ORDER_ATOMIC
    ];
};


// ============================================================
// logic_ternary
// ============================================================

zrCppGenerator.forBlock["logic_ternary"] = function(block) {

    // Ternary operator.

    const valueIf =
        this.valueToCode(
            block,
            "IF",
            this.ORDER_CONDITIONAL
        ) || "false";

    const valueThen =
        this.valueToCode(
            block,
            "THEN",
            this.ORDER_CONDITIONAL
        ) || "null";

    const valueElse =
        this.valueToCode(
            block,
            "ELSE",
            this.ORDER_CONDITIONAL
        ) || "null";

    const code =
        valueIf +
        " ? " +
        valueThen +
        " : " +
        valueElse;

    return [
        code,
        this.ORDER_CONDITIONAL
    ];
};